/**
 * QL-117 — the section model of the flow Pages tab. Pure, no React.
 *
 * A flow's pages render in five sections, drawn as nested brackets in the editor:
 *
 *   before_goals                (global, once)
 *   ┌ goal_before_portfolios    (goal, once per goal)
 *   │ ┌ portfolio               (portfolio, once per portfolio of the goal)
 *   └ goal_after_portfolios     (goal, once per goal — the position QL-117 adds)
 *   after_goals                 (global, once)
 *
 * once for the internal list and once for the public list. `order` only counts inside one
 * section. A page never leaves its bracket (its type is fixed at creation): a global page may
 * move between before/after the goals, a goal page between before/after the portfolios, and a
 * portfolio page stays put. The server enforces the same rules (flow-page-bucket.ts).
 */

import { pickT } from "@repo/shared";
import type { GlobalPosition, GoalPosition, MoveFlowPageRequest } from "@repo/shared/types";
import { normaliseGoalPosition } from "@/components/flow/goal-page-position";
import type { FlowPageWithComponents, PageType, PageVisibility } from "@/lib/types/flow";
import { T } from "./page-sections.i18n";

export type SectionKey =
  | "before_goals"
  | "goal_before_portfolios"
  | "portfolio"
  | "goal_after_portfolios"
  | "after_goals";

/** Render order of the sections — the order the flow runs its pages in. */
const SECTION_ORDER: readonly SectionKey[] = [
  "before_goals",
  "goal_before_portfolios",
  "portfolio",
  "goal_after_portfolios",
  "after_goals",
];

type SectionBucket = {
  pageType: PageType;
  globalPosition: GlobalPosition;
  goalPosition: GoalPosition;
};

const SECTION_BUCKETS: Record<SectionKey, SectionBucket> = {
  before_goals: {
    pageType: "global",
    globalPosition: "before_goals",
    goalPosition: "before_portfolios",
  },
  goal_before_portfolios: {
    pageType: "goal",
    globalPosition: "before_goals",
    goalPosition: "before_portfolios",
  },
  portfolio: {
    pageType: "portfolio",
    globalPosition: "before_goals",
    goalPosition: "before_portfolios",
  },
  goal_after_portfolios: {
    pageType: "goal",
    globalPosition: "before_goals",
    goalPosition: "after_portfolios",
  },
  after_goals: {
    pageType: "global",
    globalPosition: "after_goals",
    goalPosition: "before_portfolios",
  },
};

export type SectionLabels = {
  title: string;
  /** Muted "· meta" text after the title ("shown once, before the goals", "once per portfolio of the goal"). */
  meta: string;
  /** Show the repeat mark: the section runs more than once. */
  repeats: boolean;
  /** Offer Add Page. Off for a section kept only so its older pages stay reachable. */
  canAdd: boolean;
};

export type PageSection = SectionBucket &
  SectionLabels & {
    key: SectionKey;
    visibility: PageVisibility;
    pages: FlowPageWithComponents[];
  };

/** Cross-section drag state, lifted to the tab so every section can judge a drop. */
export type DragContext = {
  page: FlowPageWithComponents;
  sourceSection: SectionKey;
} | null;

/**
 * Section labels follow the flow's own flags, so a section never claims a repeat the flow does
 * not have. QL-205 — the global sections are "Start pages" and "End pages", placed around the
 * goal bracket, which every flow has. The goal section only mentions the portfolios when the
 * portfolio bracket is drawn between its halves.
 */
export function sectionLabels(
  lang: string,
  flags: { multiGoal: boolean; multiPortfolio: boolean } & SectionAvailability,
): Record<SectionKey, SectionLabels> {
  const t = pickT(T, lang);
  const goalTitle = flags.multiGoal ? t.forEachGoal : t.goalPages;
  const goalMeta = flags.multiGoal ? t.oncePerGoal : t.forTheGoal;
  // Older pages after the portfolios keep the goal split on a flow with no portfolio bracket.
  // The first half then reads as the plain goal section, and the second half says where its
  // pages run and takes no new ones, so it only drains.
  const legacyAfterHalf = flags.splitGoals && !flags.showPortfolio;
  return {
    before_goals: {
      title: t.startPages,
      meta: flags.multiGoal ? t.onceBeforeTheGoals : t.onceBeforeTheGoal,
      repeats: false,
      canAdd: true,
    },
    goal_before_portfolios: {
      title: goalTitle,
      meta: flags.splitGoals && flags.showPortfolio ? t.beforeThePortfolios : goalMeta,
      repeats: flags.multiGoal,
      canAdd: true,
    },
    portfolio: {
      title: flags.multiPortfolio ? t.forEachPortfolio : t.portfolioPages,
      meta: flags.multiPortfolio ? t.oncePerPortfolioOfTheGoal : t.onePortfolioPerGoal,
      repeats: flags.multiPortfolio,
      canAdd: true,
    },
    goal_after_portfolios: {
      title: goalTitle,
      meta: legacyAfterHalf ? t.afterThePagesAbove : t.afterThePortfolios,
      repeats: flags.multiGoal,
      canAdd: !legacyAfterHalf,
    },
    after_goals: {
      title: t.endPages,
      meta: flags.multiGoal ? t.onceAfterTheGoals : t.onceAfterTheGoal,
      repeats: false,
      canAdd: true,
    },
  };
}

export type SectionAvailability = {
  /** Draw the portfolio bracket. */
  showPortfolio: boolean;
  /** Split the goal bracket into before/after-the-portfolios; otherwise one goal section. */
  splitGoals: boolean;
};

/**
 * QL-205 — how much of the repeated structure a flow can use. Every instance gets a goal and a
 * default portfolio (flow-instance-service createInstanceTx), so the goal bracket is always drawn:
 * goal pages render without a risk model or a collection. The portfolio bracket needs a
 * collection, since almost every portfolio component does. Without it the goal bracket collapses
 * to a single section, as "before/after the portfolios" means nothing. A section that already
 * holds pages (in either visibility) stays visible, so its pages are never stranded.
 */
export function sectionAvailability(
  pages: FlowPageWithComponents[],
  flow: { hasPortfolioCollection: boolean },
): SectionAvailability {
  const showPortfolio =
    flow.hasPortfolioCollection || pages.some((p) => p.pageType === "portfolio");
  const splitGoals = showPortfolio || pages.some((p) => inSection(p, "goal_after_portfolios"));
  return { showPortfolio, splitGoals };
}

function inSection(page: FlowPageWithComponents, key: SectionKey): boolean {
  const bucket = SECTION_BUCKETS[key];
  if (page.pageType !== bucket.pageType) return false;
  if (bucket.pageType === "global") return page.globalPosition === bucket.globalPosition;
  // A cached pre-QL-117 payload may lack the column; it reads as "before the portfolios".
  if (bucket.pageType === "goal") {
    return normaliseGoalPosition(page.goalPosition) === bucket.goalPosition;
  }
  return true;
}

/** The five sections of one visibility, in render order, each sorted by `order` (then id). */
export function buildSections(
  pages: FlowPageWithComponents[],
  visibility: PageVisibility,
  labels: Record<SectionKey, SectionLabels>,
): PageSection[] {
  const visible = pages.filter((p) => p.visibility === visibility);
  return SECTION_ORDER.map((key) => ({
    key,
    visibility,
    ...SECTION_BUCKETS[key],
    ...labels[key],
    pages: visible
      .filter((p) => inSection(p, key))
      .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id)),
  }));
}

/** A page may only be dropped in a section of its own page type (it never leaves its bracket). */
export function canAcceptDrop(target: SectionKey, source: SectionKey): boolean {
  return SECTION_BUCKETS[target].pageType === SECTION_BUCKETS[source].pageType;
}

/** The single move request for dropping a page into `section` at `index`. */
export function moveRequestFor(section: PageSection, index: number): MoveFlowPageRequest {
  if (section.pageType === "global")
    return { globalPosition: section.globalPosition, order: index };
  return { goalPosition: section.goalPosition, order: index };
}
